<template>
  <el-container class="page">
    <AppSidebar :active-menu.sync="activeMenu" />

    <!-- 右侧内容 -->
    <el-container>
      <!-- 顶部 -->
      <el-header class="header">
        <div class="header-left">
          <i class="el-icon-user-solid"></i>
          <span class="header-title">游客信息录入</span>
        </div>

        <div class="header-right">
          <el-button icon="el-icon-refresh" @click="onReset">重置表单</el-button>
          <el-button type="primary" icon="el-icon-document-checked" @click="onSave">
            保存游客信息
          </el-button>
        </div>
      </el-header>

      <el-main class="main">
        <BaseInfoForm :form.sync="baseForm" :channel-options="settings.channelOptions" :visit-date-options="settings.visitDates" :supplier-disabled="supplierReadOnly" />
        <VisitorInfoForm
          v-model="visitors"
          :platform-options="settings.platformOptions"
          :area-options="settings.areaOptions"
          :ticket-type-options="settings.ticketTypeOptions"
        />
        <TicketTypeStats :visitors="visitors" :ticket-type-options="settings.ticketTypeOptions" :ticket-type-colors="settings.ticketTypeColors" />

        <div class="bottom-actions">
          <el-button icon="el-icon-refresh" @click="onReset">重置表单</el-button>
          <el-button type="primary" icon="el-icon-document-checked" @click="onSave">保存游客信息</el-button>
        </div>
      </el-main>
    </el-container>
  </el-container>
</template>

<script>
import AppSidebar from '../../components/AppSidebar.vue'
import BaseInfoForm from '../../components/BaseInfoForm.vue'
import VisitorInfoForm from '../../components/VisitorInfoForm.vue'
import TicketTypeStats from '../../components/TicketTypeStats.vue'

import { createOrder, getSettings } from '../../api'
import { PLATFORMS, PLATFORM_AREAS, TICKET_TYPES } from '../../config/platforms'

const createEmptyVisitor = () => ({
  name: '',
  idNo: '',
  phone: '',
  age: '系统将自动计算',
  ticketType: '',
  platform: '',
  area: '',
  remark: ''
})

export default {
  name: 'VisitorInputPage',
  components: {
    AppSidebar,
    BaseInfoForm,
    VisitorInfoForm,
    TicketTypeStats
  },
  created() {
    this.loadSettings()
  },
  data() {
    return {
      activeMenu: '1',
      baseForm: {
        channel: '',
        orderNo: '',
        visitDate: '',
        supplier: '',
        talent: '',
        commission: ''
      },
      supplierReadOnly: false,
      visitors: [createEmptyVisitor()],
      settings: {
        channelOptions: [],
        ticketTypeOptions: [],
        platformOptions: [],
        // mapping: platform -> [areas]
        areaOptions: {}
      }
    }
  },
  methods: {
    sortVisitDates(dates) {
      if (!dates || !Array.isArray(dates)) return []
      const arr = dates.slice().filter(d => !!d)
      arr.sort((a, b) => {
        const pa = String(a).replace(/\//g, '-')
        const pb = String(b).replace(/\//g, '-')
        const da = new Date(pa)
        const db = new Date(pb)
        return da - db
      })
      return arr
    },
    // 过滤掉今天之前的日期
    filterFutureDates(dates) {
      if (!dates || !Array.isArray(dates)) return []
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      return dates.filter(d => {
        if (!d) return false
        try {
          const dateStr = String(d).replace(/\//g, '-')
          const dateObj = new Date(dateStr)
          if (isNaN(dateObj.getTime())) return false
          return dateObj >= today
        } catch (e) {
          return false
        }
      })
    },
    onReset() {
      this.baseForm = {
        channel: '',
        orderNo: '',
        visitDate: '',
        supplier: '',
        talent: '',
        commission: ''
      }
      this.visitors = [createEmptyVisitor()]
    },
    async onSave() {
      // 最小校验：票种改为“每位游客必选”
      if (!this.baseForm.orderNo) {
        this.$message.warning('请输入订单号')
        return
      }
      const invalid = (this.visitors || []).some(v => {
        const ageVal = v.age
        const ageOk = typeof ageVal === 'number' || /^\d+$/.test(String(ageVal || ''))
        return !v.name || !v.idNo || !ageOk || !v.ticketType
      })
      if (invalid) {
        this.$message.warning('请完善游客信息（姓名/证件号/年龄/票种类型为必填）')
        return
      }

      try {
        const payload = {
          order: {
            orderNo: this.baseForm.orderNo,
            channel: this.baseForm.channel,
            visitDate: this.baseForm.visitDate,
            supplier: this.baseForm.supplier,
            talent: this.baseForm.talent,
            commissionRate: this.baseForm.commission
          },
          visitors: (this.visitors || []).map(v => ({
            name: v.name,
            idNo: v.idNo,
            phone: v.phone,
            age: (v.age === '' || v.age === null || v.age === undefined)
              ? undefined
              : (typeof v.age === 'number' ? v.age : parseInt(String(v.age), 10)),
            platform: v.platform,
            area: v.area,
            ticketType: v.ticketType,
            remark: v.remark || ''
          }))
        }

        await createOrder(payload)
        this.$message.success('添加成功')
        this.onReset()
      } catch (e) {
        this.$message.error(e.message || '保存失败')
      }
    },
    async loadSettings() {
      try {
        const data = await getSettings()
        if (data.visitor) {
          const ao = data.visitor.areaOptions
          let areaMap = {}
          if (ao) {
            if (Array.isArray(ao)) {
              (data.visitor.platformOptions || []).forEach(p => {
                areaMap[p] = ao.slice()
              })
            } else if (typeof ao === 'object') {
              areaMap = ao
            }
          }

          this.settings = {
            channelOptions: data.visitor.channelOptions || [],
            // 票种类型写死同步为 TICKET_TYPES
            ticketTypeOptions: TICKET_TYPES.slice(),
            // 平台与游玩区域按需求写死，不使用后端数据
            platformOptions: PLATFORMS.slice(),
            areaOptions: Object.assign({}, PLATFORM_AREAS),
            ticketTypeColors: data.visitor.ticketTypeColors || {},
            channelColors: data.visitor.channelColors || {},
            visitDates: this.sortVisitDates(this.filterFutureDates(data.visitor.visitDates || []))
          }
          // 如果当前登录用户为分销商，预填供应商单位并设为只读
          try {
            const raw = localStorage.getItem('yy_auth')
            const auth = raw ? JSON.parse(raw) : null
            const user = auth && auth.user ? auth.user : null
            if (user && user.role === '分销商') {
              this.baseForm.supplier = user.supplier || ''
              this.supplierReadOnly = true
            } else {
              this.supplierReadOnly = false
            }
          } catch (e) {
            this.supplierReadOnly = false
          }
        }
      } catch (e) {
        console.error('加载设置失败:', e)
        // 使用默认设置
        this.settings = {
          channelOptions: ['线上平台', '线下代理', '旅行社', '直客'],
          ticketTypeOptions: ['成人票', '儿童票', '老人票', '学生票', '军人票', '残疾人票'],
          platformOptions: ['主会场', '分会场A', '分会场B', '分会场C'],
          areaOptions: {
            '主会场': ['VIP区', '普通区', '亲子区', '观景台', '休息区'],
            '分会场A': [],
            '分会场B': [],
            '分会场C': []
          }
        }
      }
    }
  }
}
</script>

<style scoped>
.page {
  height: 100vh;
  background: #f5f7fa;
}

.header {
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #ebeef5;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #303133;
}

.header-title {
  font-size: 18px;
  font-weight: 700;
}

.main {
  padding: 16px;
}

.bottom-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 8px 0 20px;
}
</style>
